/**
 * API client for the admin-only timetoinvoice integration settings.
 *
 * The API key is write-only: it goes out in a PUT / POST body and never
 * comes back. Every response type here is secret-free by construction —
 * `apiKey` on the status is an object with `present` and `fingerprint`
 * members only, and there is deliberately no field that could hold a value.
 */

import axios from 'axios';
import { apiClient, handleApiError } from './apiClient';

const INTEGRATION_PATH = '/admin/integrations/timetoinvoice';
const HTTP_FORBIDDEN = 403;
const HTTP_UNPROCESSABLE = 422;
const TEST_FAILED_CODE = 'TIMETOINVOICE_TEST_FAILED';

export type IntegrationSource = 'stored' | 'env' | 'none';
export type IntegrationHealth = 'ok' | 'unconfigured' | 'error';
export type IntegrationTestStatus =
  | 'ok'
  | 'unauthorised'
  | 'not_found'
  | 'unreachable'
  | 'timeout'
  | 'bad_payload'
  | 'no_snapshot';

/** Result of a connection probe — always data, never an HTTP failure */
export interface IntegrationTestResult {
  ok: boolean;
  status: IntegrationTestStatus;
  message: string;
  checkedAt: string;
  /** Open billing cycle keyed by its end month (YYYY-MM), when known */
  cycleKey?: string;
}

/** GET shape. Secret-free: the key itself has no member here. */
export interface IntegrationStatus {
  id: 'timetoinvoice';
  source: IntegrationSource;
  status: IntegrationHealth;
  enabled: boolean;
  baseUrl: string | null;
  timeoutMs: number;
  apiKey: { present: boolean; fingerprint: string | null };
  updatedAt: string | null;
  updatedBy: string | null;
  lastTest: {
    at: string;
    ok: boolean;
    status: IntegrationTestStatus;
    cycleKey?: string;
  } | null;
}

export interface SaveIntegrationInput {
  baseUrl: string;
  timeoutMs?: number;
  /** Omit to keep the stored key; the client never holds the stored value */
  apiKey?: string;
  clearApiKey?: boolean;
  skipTest?: boolean;
}

export interface TestIntegrationInput {
  baseUrl?: string;
  apiKey?: string;
}

/** The server probed the candidate values and refused to persist them */
export class IntegrationTestFailedError extends Error {
  readonly result: IntegrationTestResult;

  constructor(result: IntegrationTestResult) {
    super(result.message);
    this.name = 'IntegrationTestFailedError';
    this.result = result;
  }
}

/** The caller is signed in but is not an administrator */
export class IntegrationForbiddenError extends Error {
  constructor() {
    super('Only administrators can change integrations.');
    this.name = 'IntegrationForbiddenError';
  }
}

interface ErrorEnvelope {
  error?: { code?: string; message?: string; details?: unknown };
}

function isTestResult(value: unknown): value is IntegrationTestResult {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.ok === 'boolean' &&
    typeof candidate.status === 'string' &&
    typeof candidate.message === 'string'
  );
}

/**
 * `details` arrives as `{ result: <probe object> }` on a 422
 * TIMETOINVOICE_TEST_FAILED (IntegrationTestFailedError wraps it one
 * level deep) - check that shape first, and keep the flatter shapes as
 * a fallback so a future server change degrades rather than breaks the
 * "Save anyway" flow outright.
 */
function readTestResult(details: unknown): IntegrationTestResult | null {
  if (typeof details === 'object' && details !== null && 'result' in details) {
    const wrapped = (details as { result?: unknown }).result;
    if (isTestResult(wrapped)) return wrapped;
  }
  const candidate = Array.isArray(details) ? details[0] : details;
  return isTestResult(candidate) ? candidate : null;
}

/**
 * Turn the two responses the UI must act on into typed errors; everything
 * else goes through the shared handler so the message copy stays uniform.
 */
function throwIntegrationError(error: unknown, fallback: string): never {
  if (axios.isAxiosError<ErrorEnvelope>(error) && error.response) {
    const { status, data } = error.response;
    if (status === HTTP_FORBIDDEN) {
      throw new IntegrationForbiddenError();
    }
    if (status === HTTP_UNPROCESSABLE && data?.error?.code === TEST_FAILED_CODE) {
      const result = readTestResult(data.error.details);
      if (result) throw new IntegrationTestFailedError(result);
    }
  }
  return handleApiError(error, fallback);
}

export async function fetchIntegrationStatus(): Promise<IntegrationStatus> {
  try {
    const response = await apiClient.get<{ data: IntegrationStatus }>(INTEGRATION_PATH);
    return response.data.data;
  } catch (error) {
    return throwIntegrationError(error, 'Could not load integration settings.');
  }
}

export async function saveIntegration(input: SaveIntegrationInput): Promise<IntegrationStatus> {
  try {
    const response = await apiClient.put<{ data: IntegrationStatus }>(INTEGRATION_PATH, input);
    return response.data.data;
  } catch (error) {
    return throwIntegrationError(error, 'Could not save the connection.');
  }
}

export async function testIntegration(
  input: TestIntegrationInput = {}
): Promise<IntegrationTestResult> {
  try {
    const response = await apiClient.post<{ data: IntegrationTestResult }>(
      `${INTEGRATION_PATH}/test`,
      input
    );
    return response.data.data;
  } catch (error) {
    return throwIntegrationError(error, 'Could not test the connection.');
  }
}

export async function removeIntegration(): Promise<IntegrationStatus> {
  try {
    const response = await apiClient.delete<{ data: IntegrationStatus }>(INTEGRATION_PATH);
    return response.data.data;
  } catch (error) {
    return throwIntegrationError(error, 'Could not remove the connection.');
  }
}
